import { apiDate, PDF_FILE_TYPE } from '@/platform/lib/utils';
import {
    Button,
    Checkbox,
    Container,
    Divider,
    Dropdown,
    Form,
    FormGroup,
    Input,
    InputPlaceholder,
    Message,
    Segment,
    SegmentContent,
    SegmentHeader,
    Select,
    Textarea,
} from '@/platform/ui';
import {
    ArrowLeftIcon,
    MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { Deferred, Link, useForm, usePage } from '@inertiajs/react';
import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import ContractTypes from './components/ContractTypesList';
import EntitiesList from './components/EntitiesList';
import KVKSearchModal from './components/KVKSearchModal';

const services = [
    {
        id: 1,
        name: 'Premie & subsidiemonitor',
    },
    {
        id: 2,
        name: 'Controle gedifferentieerde premie TWK',
    },
    {
        id: 3,
        name: 'Payroll monitor TWK',
    },
    {
        id: 4,
        name: 'AGH-SFB onderzoek TWK',
    },
];

const CreateOrUpdateView = () => {
    const [kvkModal, setKvkModal] = useState(null);

    const { contract, is_vcsw, customers, managers, classifications, years } =
        usePage().props;
    const { data, setData, post, errors, processing, transform } = useForm({});

    useEffect(() => {
        if (contract?.id) {
            setData(contract);
        }
    }, [contract?.id]);

    useEffect(() => {
        if (!data?.customer_id || data?.id) {
            return;
        }

        axios
            .get(
                `/api/customers/${data?.customer_id}?with_contacts=1&no_legacy=1`
            )
            .then(({ data: customer }) => {
                const dataToSet = {
                    company_name: customer?.name || '',
                    fee: customer?.price_type || '',
                    invoice_email: customer?.invoice_email || '',
                    connection_costs: customer?.connection_costs || '',
                    kvk_number: customer?.entities[0]?.kvk || '',
                };
                dataToSet.entities = [];

                if (customer?.contacts?.length > 0) {
                    dataToSet.contact = customer?.contacts[0]?.name || '';
                    dataToSet.contact_email =
                        customer?.contacts[0]?.email || '';
                    dataToSet.contact_phone =
                        customer?.contacts[0]?.telephone_number || '';
                }

                customer?.entities?.map(entity => {
                    dataToSet.entities.push({
                        id: entity.id,
                        name: entity?.name || '',
                        kvk: entity?.kvk || '',
                        city: entity?.city || '',
                        lh: entity?.loonheffingennummer || '',
                    });
                });

                setData({
                    ...data,
                    ...dataToSet,
                });
            });
    }, [data?.customer_id]);

    const title = useMemo(
        () => (contract?.id ? 'Wijzig contract' : 'Nieuwe contract'),
        [contract]
    );

    const handleKvkSearch = company => {
        if (kvkModal === 'entities') {
            setData('entities', [
                ...(data?.entities || []),
                {
                    id: `new-${Date.now()}`,
                    name: company?.company || '',
                    kvk: company?.kvk_number || '',
                    city: company?.city || '',
                    lh: company?.lh || '',
                },
            ]);

            setKvkModal(null);

            return;
        }

        const dataToSet = {
            ...data,
            kvk_number: company?.kvk_number || '',
            company_name: company?.company || '',
            address: `${company?.street} ${company?.house_number}`,
            zipcode: company?.zipcode || '',
            city: company?.city || '',
            entities: [
                ...(data?.entities || []),
                {
                    id: `new-${Date.now()}`,
                    name: company?.company || '',
                    kvk: company?.kvk_number || '',
                    city: company?.city || '',
                    lh: company?.lh || '',
                },
            ],
        };

        setData(dataToSet);

        setKvkModal(null);
    };

    const handleSubmit = () => {
        if (contract?.id) {
            transform(data => ({
                ...data,
                _method: 'patch',
            }));

            post(`/contracts/${contract.id}`);

            return;
        }

        post('/contracts');
    };

    return (
        <Container>
            <Form onSubmit={handleSubmit}>
                <div className='grid grid-cols-1 gap-4 lg:grid-cols-3'>
                    <Segment className='self-start lg:col-span-2'>
                        <SegmentHeader header={title}>
                            <Button
                                variant='primary'
                                onClick={() => setKvkModal('customers')}
                            >
                                <MagnifyingGlassIcon className='size-4' />
                                Zoeken in KvK
                            </Button>
                            <Button as={Link} href='/contracts'>
                                <ArrowLeftIcon className='size-5' />
                                Terug
                            </Button>
                        </SegmentHeader>
                        <SegmentContent>
                            <FormGroup>
                                {!data?.id && (
                                    <Deferred
                                        data='customers'
                                        fallback={
                                            <InputPlaceholder label='Bestaande of nieuwe klant' />
                                        }
                                    >
                                        <Dropdown
                                            label='Bestaande of nieuwe klant'
                                            placeholder='Nieuwe klant'
                                            options={customers}
                                            value={data?.customer_id || ''}
                                            onChange={value =>
                                                setData('customer_id', value)
                                            }
                                            error={errors?.customer_id}
                                        />
                                    </Deferred>
                                )}
                                {!is_vcsw && (
                                    <Deferred
                                        data='managers'
                                        fallback={
                                            <InputPlaceholder label='Relatiebeheerder(s)' />
                                        }
                                    >
                                        <Dropdown
                                            label='Relatiebeheerder(s)'
                                            multiple
                                            options={managers || []}
                                            value={data?.managers || []}
                                            onChange={value =>
                                                setData('managers', value)
                                            }
                                            error={errors?.managers}
                                        />
                                    </Deferred>
                                )}
                                <Input
                                    label='Ibis-relatienummer Aon intern'
                                    value={data?.project_number || ''}
                                    onChange={event =>
                                        setData(
                                            'project_number',
                                            event.target.value
                                        )
                                    }
                                    error={errors?.project_number}
                                />
                                <Input
                                    label='Bedrijfsnaam'
                                    value={data?.company_name || ''}
                                    onChange={event =>
                                        setData(
                                            'company_name',
                                            event.target.value
                                        )
                                    }
                                    error={errors?.company_name}
                                />
                                <Input
                                    label='KvK nummer'
                                    value={data?.kvk_number || ''}
                                    onChange={event =>
                                        setData(
                                            'kvk_number',
                                            event.target.value
                                        )
                                    }
                                    error={errors?.kvk_number}
                                />
                                <Input
                                    label='Straat + Huisnummer'
                                    value={data?.address || ''}
                                    onChange={event =>
                                        setData('address', event.target.value)
                                    }
                                    error={errors?.address}
                                />
                                <Input
                                    label='Postcode'
                                    value={data?.zipcode || ''}
                                    onChange={event =>
                                        setData('zipcode', event.target.value)
                                    }
                                    error={errors?.zipcode}
                                />
                                <Input
                                    label='Plaats'
                                    value={data?.city || ''}
                                    onChange={event =>
                                        setData('city', event.target.value)
                                    }
                                    error={errors?.city}
                                />
                                <Input
                                    label='Vertegenwoordiger'
                                    value={data?.represented_by || ''}
                                    onChange={event =>
                                        setData(
                                            'represented_by',
                                            event.target.value
                                        )
                                    }
                                    error={errors?.represented_by}
                                />
                                <Input
                                    label='E-mailadres vertegenwoordiger'
                                    type='email'
                                    required
                                    value={data?.represented_by_email || ''}
                                    onChange={event =>
                                        setData(
                                            'represented_by_email',
                                            event.target.value
                                        )
                                    }
                                    error={errors?.represented_by_email}
                                />
                                <Input
                                    label='Contactpersoon'
                                    value={data?.contact || ''}
                                    onChange={event =>
                                        setData('contact', event.target.value)
                                    }
                                    error={errors?.contact}
                                />
                                <Input
                                    label='E-mailadres contactpersoon'
                                    type='email'
                                    value={data?.contact_email || ''}
                                    onChange={event =>
                                        setData(
                                            'contact_email',
                                            event.target.value
                                        )
                                    }
                                    error={errors?.contact_email}
                                />
                                <Input
                                    label='Telefoonnummer contactpersoon'
                                    value={data?.contact_phone || ''}
                                    onChange={event =>
                                        setData(
                                            'contact_phone',
                                            event.target.value
                                        )
                                    }
                                    error={errors?.contact_phone}
                                />
                                {is_vcsw && (
                                    <Input
                                        label='Factuur e-mailadres'
                                        type='email'
                                        value={data?.invoice_email || ''}
                                        onChange={event =>
                                            setData(
                                                'invoice_email',
                                                event.target.value
                                            )
                                        }
                                        error={errors?.invoice_email}
                                    />
                                )}
                                {!is_vcsw && (
                                    <Dropdown
                                        label='Diensten'
                                        options={services}
                                        multiple
                                        value={data?.services || []}
                                        onChange={value =>
                                            setData('services', value)
                                        }
                                    />
                                )}
                                {data?.services?.includes(2) && (
                                    <Message
                                        variant='error'
                                        content='Bij deze dienstverlening worden
                                            (ex)-werknemers van de klant
                                            benaderd voor het invullen van
                                            een vragenlijst. Met de
                                            vragenlijst brengen wij een
                                            eventueel uitkeringsverleden in
                                            kaart. Zorg ervoor dat de klant
                                            hiervan op de hoogte is en
                                            bespreek eventuele privacy
                                            vraagstukken.'
                                    />
                                )}
                                {(is_vcsw || data?.services?.includes(2)) && (
                                    <>
                                        <Input
                                            label='Aansluitkosten per medewerker'
                                            value={data?.connection_costs || ''}
                                            onChange={event =>
                                                setData(
                                                    'connection_costs',
                                                    event.target.value
                                                )
                                            }
                                            error={errors?.connection_costs}
                                        />
                                        <Input
                                            label='Aantal medewerkers'
                                            type='number'
                                            value={data?.employee_count || ''}
                                            onChange={event =>
                                                setData(
                                                    'employee_count',
                                                    event.target.value
                                                )
                                            }
                                            error={errors?.employee_count}
                                        />
                                    </>
                                )}
                                {is_vcsw && (
                                    <>
                                        <Input
                                            label='Fee'
                                            value={data?.fee || ''}
                                            onChange={event =>
                                                setData(
                                                    'fee',
                                                    event.target.value
                                                )
                                            }
                                            error={errors?.fee}
                                        />
                                        <Input
                                            label='Fee SPL'
                                            value={data?.fee_spl || ''}
                                            onChange={event =>
                                                setData(
                                                    'fee_spl',
                                                    event.target.value
                                                )
                                            }
                                            error={errors?.fee_spl}
                                        />
                                        <Input
                                            name='fee_no_risk'
                                            label='Fee no-risk'
                                            value={data?.fee_no_risk || ''}
                                            onChange={event =>
                                                setData(
                                                    'fee_no_risk',
                                                    event.target.value
                                                )
                                            }
                                            error={errors?.fee_no_risk}
                                        />
                                    </>
                                )}
                                {!is_vcsw && (
                                    <Input
                                        label='No cure no pay percentage Aon'
                                        value={data?.fee_aon || ''}
                                        onChange={event =>
                                            setData(
                                                'fee_aon',
                                                event.target.value
                                            )
                                        }
                                        error={errors?.fee_aon}
                                    />
                                )}
                                <Dropdown
                                    label='Onderzoeken starten vanaf jaar'
                                    options={years || []}
                                    value={Number(data?.year || '')}
                                    onChange={value => setData('year', value)}
                                    error={errors?.year}
                                />
                                {(is_vcsw || data?.services?.includes(2)) && (
                                    <>
                                        <Input
                                            label={`Startdatum ${is_vcsw ? 'zekerheid' : 'premie & subsidiemonitor'}`}
                                            type='date'
                                            value={
                                                apiDate(
                                                    data?.start_zekerheid
                                                ) || ''
                                            }
                                            onChange={event =>
                                                setData(
                                                    'start_zekerheid',
                                                    event.target.value
                                                )
                                            }
                                            error={errors?.start_zekerheid}
                                        />
                                        <FormGroup equal inline>
                                            <Input
                                                label={`Contractduur ${is_vcsw ? 'zekerheid' : 'premie & subsidiemonitor'}`}
                                                type='number'
                                                value={
                                                    data?.contract_duration ||
                                                    ''
                                                }
                                                onChange={event =>
                                                    setData(
                                                        'contract_duration',
                                                        event.target.value
                                                    )
                                                }
                                                error={
                                                    errors?.contract_duration
                                                }
                                            />
                                            <Checkbox
                                                className='self-end'
                                                label='Onbepaald'
                                                checked={
                                                    data?.contract_duration == 0
                                                }
                                                onChange={checked =>
                                                    setData(
                                                        'contract_duration',
                                                        checked ? 0 : 1
                                                    )
                                                }
                                            />
                                        </FormGroup>
                                        <Deferred
                                            data='classifications'
                                            fallback={
                                                <InputPlaceholder label='Standaard Bedrijfsindeling' />
                                            }
                                        >
                                            <Select
                                                label='Standaard Bedrijfsindeling'
                                                options={classifications || []}
                                                value={
                                                    data?.customer
                                                        ?.standard_business_classification_id ||
                                                    ''
                                                }
                                                onChange={event =>
                                                    setData(
                                                        'customer.standard_business_classification_id',
                                                        event.target.value
                                                    )
                                                }
                                                error={
                                                    errors?.[
                                                        'customer.standard_business_classification_id'
                                                    ]
                                                }
                                            />
                                        </Deferred>
                                        <Input
                                            label='Verwacht aantal werknemers'
                                            value={
                                                data?.customer
                                                    ?.sales_expected_employee_count ||
                                                ''
                                            }
                                            onChange={event =>
                                                setData(
                                                    'customer.sales_expected_employee_count',
                                                    event.target.value
                                                )
                                            }
                                            error={
                                                errors?.[
                                                    'customer.sales_expected_employee_count'
                                                ]
                                            }
                                        />
                                        <Input
                                            name='customer.sales_chamber_of_commerce_number'
                                            label='KvK nummer (Sales)'
                                            value={
                                                data?.customer
                                                    ?.sales_chamber_of_commerce_number ||
                                                ''
                                            }
                                            onChange={event =>
                                                setData(
                                                    'customer.sales_chamber_of_commerce_number',
                                                    event.target.value
                                                )
                                            }
                                            error={
                                                errors?.[
                                                    'customer.sales_chamber_of_commerce_number'
                                                ]
                                            }
                                        />
                                    </>
                                )}
                                {!is_vcsw && (
                                    <Textarea
                                        label='Opmerkingen'
                                        value={data?.comments || ''}
                                        onChange={event =>
                                            setData(
                                                'comments',
                                                event.target.value
                                            )
                                        }
                                        error={errors?.comments}
                                    />
                                )}
                            </FormGroup>
                        </SegmentContent>
                    </Segment>

                    <div className='flex flex-col gap-4'>
                        <EntitiesList
                            entities={data?.entities || []}
                            onChange={setData}
                            onModalOpen={() => setKvkModal('entities')}
                        />

                        <ContractTypes
                            data={data}
                            onChange={setData}
                            errors={errors}
                        />

                        {!is_vcsw && (
                            <>
                                <Divider content='Documenten' />
                                <Segment>
                                    <SegmentContent>
                                        <Input
                                            label='Machtiging meerdere entiteiten'
                                            type='file'
                                            accept={PDF_FILE_TYPE}
                                            onChange={event =>
                                                setData(
                                                    'uploads.machtiging',
                                                    event.target.files[0]
                                                )
                                            }
                                            error={
                                                errors?.['uploads.machtiging']
                                            }
                                        />
                                    </SegmentContent>
                                </Segment>

                                <Segment>
                                    <SegmentContent>
                                        <Input
                                            label='Overeenkomst'
                                            type='file'
                                            accept={PDF_FILE_TYPE}
                                            onChange={event =>
                                                setData(
                                                    'uploads.overeenkomst',
                                                    event.target.files[0]
                                                )
                                            }
                                            error={
                                                errors?.['uploads.overeenkomst']
                                            }
                                        />
                                    </SegmentContent>
                                </Segment>
                            </>
                        )}
                    </div>
                </div>

                <Segment floating>
                    <SegmentContent right>
                        <Button
                            type='submit'
                            variant='primary'
                            loading={processing}
                        >
                            Opslaan
                        </Button>
                    </SegmentContent>
                </Segment>
            </Form>

            <KVKSearchModal
                open={!!kvkModal}
                onClose={() => setKvkModal(null)}
                onChange={handleKvkSearch}
            />
        </Container>
    );
};

export default CreateOrUpdateView;
